import { Component } from '@theme/component';

/**
 * Simple accessible tabs component used by the Product Included Info section.
 *
 * Markup contract:
 *   <blume-tabs>
 *     <div ref="tablist" role="tablist">
 *       <button ref="tabs[]" role="tab" id="tab-X" aria-controls="panel-X" on:click="/select?index=0">...</button>
 *       ...
 *     </div>
 *     <div ref="panels[]" role="tabpanel" id="panel-X" aria-labelledby="tab-X">...</div>
 *     ...
 *   </blume-tabs>
 *
 * Behavior:
 *   - Click or Enter/Space activates the tab under the cursor/focus.
 *   - Arrow keys move focus between tabs (and activate immediately).
 *   - Home/End jump to first/last tab.
 *   - Active panel has `hidden` removed; inactive panels stay in the DOM but hidden.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} tablist
 * @property {HTMLButtonElement[]} tabs
 * @property {HTMLElement[]} panels
 *
 * @extends Component<Refs>
 */
class BlumeTabsComponent extends Component {
  requiredRefs = ['tablist', 'tabs', 'panels'];

  /** @type {number} */
  #current = 0;

  connectedCallback() {
    super.connectedCallback();
    this.refs.tablist.addEventListener('keydown', this.#handleKey);
    this.#activate(this.#findInitialIndex(), { focus: false });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.tablist.removeEventListener('keydown', this.#handleKey);
  }

  /**
   * Triggered by `on:click="/select?index=N"`.
   * @param {{ index: string | number }} data
   */
  select(data) {
    const index = Number(data?.index);
    if (Number.isNaN(index)) return;
    this.#activate(index, { focus: false });
  }

  #findInitialIndex() {
    const fromAttr = Number(this.dataset.initialIndex);
    if (!Number.isNaN(fromAttr) && fromAttr >= 0 && fromAttr < this.refs.tabs.length) {
      return fromAttr;
    }
    return 0;
  }

  /**
   * @param {number} index
   * @param {{ focus?: boolean }} [options]
   */
  #activate(index, { focus = true } = {}) {
    const { tabs, panels } = this.refs;
    if (index < 0 || index >= tabs.length) return;

    for (let i = 0; i < tabs.length; i++) {
      const tab = tabs[i];
      const panel = panels[i];
      const active = i === index;
      if (tab) {
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.tabIndex = active ? 0 : -1;
      }
      if (panel) {
        panel.hidden = !active;
      }
    }

    this.#current = index;
    if (focus) tabs[index]?.focus();
  }

  /** @param {KeyboardEvent} event */
  #handleKey = (event) => {
    const total = this.refs.tabs.length;
    let next = this.#current;

    switch (event.key) {
      case 'ArrowRight':
        next = (this.#current + 1) % total;
        break;
      case 'ArrowLeft':
        next = (this.#current - 1 + total) % total;
        break;
      case 'Home':
        next = 0;
        break;
      case 'End':
        next = total - 1;
        break;
      default:
        return;
    }

    event.preventDefault();
    this.#activate(next);
  };
}

if (!customElements.get('blume-tabs')) {
  customElements.define('blume-tabs', BlumeTabsComponent);
}
